import { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { getStaffScheduleView, getStudentScheduleView } from "../../lib/api";
import { printPage } from "../../lib/print";
import { DAYS, DAY_LABELS, normCell } from "./constants";

const esc = (s) => String(s == null ? "" : s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

function OptionRow({ value, label, hint, mode, onSelect }) {
  return (
    <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${mode === value ? "border-[#00305E] bg-[#00305E]/5" : "hover:bg-muted/50"}`} data-testid={`print-mode-${value}`}>
      <input type="radio" name="print-mode" className="mt-1 accent-[#00305E]" checked={mode === value} onChange={() => onSelect(value)} />
      <div>
        <div className="font-medium text-sm">{label}</div>
        {hint && <div className="text-xs text-muted-foreground">{hint}</div>}
      </div>
    </label>
  );
}

// Shared schedule table styles for the print window.
const SCHEDULE_STYLES = `
  .sched-section { page-break-inside: auto; }
  .sched-section + .sched-section { page-break-before: always; }
  .sched-name { font-size: 16px; font-weight: 700; color: #00305E; margin: 4px 0 2px; }
  .sched-meta { font-size: 11px; color: #666; margin-bottom: 8px; }
  table.sched { width: 100%; border-collapse: collapse; margin: 0 0 8px; table-layout: fixed; }
  table.sched th, table.sched td { border: 1.2px solid #444; padding: 4px 6px; font-size: 10px; vertical-align: top; }
  table.sched th { background: #e5e7eb; font-weight: 700; text-align: center; }
  table.sched td.blk { background: #f5f5f5; font-weight: 600; width: 90px; }
  .subj { font-weight: 700; color: #00305E; }
  .role-lead { color: #15803d; font-weight: 700; }
  .role-support { color: #2563eb; }
  .muted { color: #888; font-style: italic; }
  .free { color: #16a34a; font-weight: 600; }
  .hl { background: #FEF3C7; border-left: 3px solid #F59E0B; padding: 1px 3px; }
`;

export default function SchedulePrintDialog({
  open, onOpenChange, activeScheduleId, schedule, blocks,
  staffList, studentList, staffLookup, studentLookup, onPrintWholeWeek,
}) {
  const [mode, setMode] = useState("week"); // week | staff | student | all_staff | all_students
  const [selId, setSelId] = useState("");
  const [busy, setBusy] = useState(false);

  const fridayHalf = !!schedule?.friday_half_day;
  const dayBlockKeys = (day) => {
    const list = (day === "friday" && fridayHalf) ? (blocks.half || []) : (blocks.full || []);
    return new Set(list.map((b) => b.name.toLowerCase().replace(/\s/g, "")));
  };
  const rowBlocks = blocks.full || [];

  const scheduleMeta = () => `${esc(schedule?.name || "Master Schedule")} &middot; Printed ${new Date().toLocaleDateString()}`;

  // Build one staff schedule table (grid: blocks x days).
  const staffTableHTML = (name, entries) => {
    let body = "";
    rowBlocks.forEach((block) => {
      const key = block.name.toLowerCase().replace(/\s/g, "");
      let tds = `<td class="blk">${esc(block.name)}<br><span class="muted">${esc(block.start_time || "")}-${esc(block.end_time || "")}</span></td>`;
      DAYS.forEach((day) => {
        if (!dayBlockKeys(day).has(key)) { tds += `<td style="text-align:center;color:#aaa;">--</td>`; return; }
        const cell = normCell(entries[`${day}_${key}`]);
        if (!cell.length) { tds += `<td><span class="free">Free / Planning</span></td>`; return; }
        tds += `<td>${cell.map((e) => `<div><span class="subj">${esc(e.subject)}</span><br>Role: <span class="${e.role === "Lead" ? "role-lead" : "role-support"}">${esc(e.role)}</span>${e.student_group ? `<br><span class="muted">${esc(e.student_group)}</span>` : ""}</div>`).join('<hr style="border:none;border-top:1px dashed #ccc;margin:2px 0;">')}</td>`;
      });
      body += `<tr>${tds}</tr>`;
    });
    const head = `<tr><th class="blk">Block</th>${DAYS.map((d) => `<th>${DAY_LABELS[d]}</th>`).join("")}</tr>`;
    return `<div class="sched-section"><div class="sched-name">${esc(name)}'s Schedule</div><div class="sched-meta">${scheduleMeta()}</div><table class="sched"><thead>${head}</thead><tbody>${body}</tbody></table></div>`;
  };

  // Build one student schedule table (advisory row + blocks).
  const studentTableHTML = (name, entries) => {
    const renderCell = (cell) => {
      if (!cell || !cell.length) return `<td style="text-align:center;color:#aaa;">--</td>`;
      return `<td>${cell.map((e) => `<div class="${e.highlight ? "hl" : ""}"><span class="subj">${esc(e.subject)}</span>${e.lead ? `<br>${esc(e.lead)}` : ""}${e.student_group ? `<br><span class="muted">${esc(e.student_group)}</span>` : ""}${e.location ? `<br><span class="muted">${esc(e.location)}</span>` : ""}</div>`).join('<hr style="border:none;border-top:1px dashed #ccc;margin:2px 0;">')}</td>`;
    };
    let body = `<tr><td class="blk">Advisory<br><span class="muted">8:45-9:00</span></td>${DAYS.map((day) => renderCell(entries[`${day}_advisory`])).join("")}</tr>`;
    rowBlocks.forEach((block) => {
      const key = block.name.toLowerCase().replace(/\s/g, "");
      let tds = `<td class="blk">${esc(block.name)}<br><span class="muted">${esc(block.start_time || "")}-${esc(block.end_time || "")}</span></td>`;
      DAYS.forEach((day) => {
        if (!dayBlockKeys(day).has(key)) { tds += `<td style="text-align:center;color:#aaa;">--</td>`; return; }
        tds += renderCell(entries[`${day}_${key}`]);
      });
      body += `<tr>${tds}</tr>`;
    });
    const head = `<tr><th class="blk">Block</th>${DAYS.map((d) => `<th>${DAY_LABELS[d]}</th>`).join("")}</tr>`;
    return `<div class="sched-section"><div class="sched-name">${esc(name)}'s Schedule</div><div class="sched-meta">${scheduleMeta()}</div><table class="sched"><thead>${head}</thead><tbody>${body}</tbody></table></div>`;
  };

  const doPrint = async () => {
    if (mode === "week") {
      onOpenChange(false);
      onPrintWholeWeek();
      return;
    }
    if ((mode === "staff" || mode === "student") && !selId) {
      toast.error(`Please choose a ${mode === "staff" ? "staff member" : "student"}`);
      return;
    }
    setBusy(true);
    try {
      let content = "";
      let title = "";
      if (mode === "staff") {
        const res = await getStaffScheduleView(activeScheduleId, selId);
        content = staffTableHTML(staffLookup[selId] || res.data.staff_name || "Staff", res.data.entries || {});
        title = `${staffLookup[selId] || "Staff"} — Schedule`;
      } else if (mode === "student") {
        const res = await getStudentScheduleView(activeScheduleId, selId);
        content = studentTableHTML(res.data.student_name || "Student", res.data.entries || {});
        title = `${res.data.student_name || "Student"} — Schedule`;
      } else if (mode === "all_staff") {
        const results = await Promise.all(staffList.map((s) => getStaffScheduleView(activeScheduleId, s.id).then((r) => ({ s, r }))));
        content = results.map(({ s, r }) => staffTableHTML(staffLookup[s.id] || s.name, r.data.entries || {})).join("");
        title = "All Staff Schedules";
      } else if (mode === "all_students") {
        const results = await Promise.all(studentList.map((s) => getStudentScheduleView(activeScheduleId, s.id).then((r) => ({ s, r }))));
        content = results.map(({ r }) => studentTableHTML(r.data.student_name || "Student", r.data.entries || {})).join("");
        title = "All Student Schedules";
      }
      printPage({ title, subtitle: esc(schedule?.name || "Master Schedule"), content, extraStyles: SCHEDULE_STYLES, landscape: true });
      onOpenChange(false);
    } catch (e) {
      console.error("Schedule print build failed:", e);
      toast.error("Failed to build the printout");
    } finally {
      setBusy(false);
    }
  };

  const OPTS = [
    { value: "week", label: "Whole week (master grid)", hint: "The full week for all classes" },
    { value: "staff", label: "A staff member", hint: "One person's weekly schedule" },
    { value: "student", label: "A student", hint: "One student's weekly schedule" },
    { value: "all_staff", label: "All staff", hint: "Every staff schedule, one per page" },
    { value: "all_students", label: "All students", hint: "Every student schedule, one per page" },
  ];
  const selectMode = (v) => { setMode(v); setSelId(""); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="print-options-dialog">
        <DialogHeader>
          <DialogTitle>Print Schedule</DialogTitle>
          <DialogDescription>Choose what to print for {schedule?.name || "this schedule"}.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 pt-1">
          {OPTS.map((o) => <OptionRow key={o.value} value={o.value} label={o.label} hint={o.hint} mode={mode} onSelect={selectMode} />)}

          {mode === "staff" && (
            <div className="space-y-1 pt-1">
              <Label className="text-xs">Staff member</Label>
              <Select value={selId} onValueChange={setSelId}>
                <SelectTrigger data-testid="print-staff-select"><SelectValue placeholder="Choose a staff member..." /></SelectTrigger>
                <SelectContent>{staffList.map((s) => <SelectItem key={s.id} value={s.id}>{staffLookup[s.id] || s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          {mode === "student" && (
            <div className="space-y-1 pt-1">
              <Label className="text-xs">Student</Label>
              <Select value={selId} onValueChange={setSelId}>
                <SelectTrigger data-testid="print-student-select"><SelectValue placeholder="Choose a student..." /></SelectTrigger>
                <SelectContent>{studentList.map((s) => <SelectItem key={s.id} value={s.id}>{studentLookup[s.id] || s.preferred_name || s.first_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="bg-[#00305E] hover:bg-[#00305E]/90" onClick={doPrint} disabled={busy} data-testid="print-confirm-btn">
            {busy ? "Preparing..." : "Print"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
